export interface VaultSecuritySettings {
  session_timeout_minutes: number;
  password_min_length: number;
  require_uppercase: boolean;
  require_numbers: boolean;
  require_special_chars: boolean;
  two_factor_enabled: boolean;
}

export const VAULT_SECURITY_SETTINGS_STORAGE_KEY = 'security_settings';

export const DEFAULT_VAULT_SECURITY_SETTINGS: VaultSecuritySettings = {
  session_timeout_minutes: 30,
  password_min_length: 8,
  require_uppercase: false,
  require_numbers: false,
  require_special_chars: false,
  two_factor_enabled: false,
};

export const VAULT_SECURITY_SETTINGS_UPDATED_EVENT = 'vault-security-settings-updated';

/** Mínimo que exige Simappe al provisionar usuarios (independiente de la política local). */
export const SIMAPPE_PASSWORD_MIN_LENGTH = 8;

/** Máximo que acepta Simappe al provisionar usuarios. */
export const SIMAPPE_PASSWORD_MAX_LENGTH = 255;

function readPartialSecuritySettings(): Partial<VaultSecuritySettings> | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(VAULT_SECURITY_SETTINGS_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Partial<VaultSecuritySettings>;
  } catch {
    return null;
  }
}

export function readVaultSecuritySettings(): VaultSecuritySettings {
  const saved = readPartialSecuritySettings();
  return {
    ...DEFAULT_VAULT_SECURITY_SETTINGS,
    ...saved,
    session_timeout_minutes: clampSessionTimeoutMinutes(
      saved?.session_timeout_minutes ?? DEFAULT_VAULT_SECURITY_SETTINGS.session_timeout_minutes,
    ),
    password_min_length: clampPasswordMinLength(
      saved?.password_min_length ?? DEFAULT_VAULT_SECURITY_SETTINGS.password_min_length,
    ),
  };
}

export function clampSessionTimeoutMinutes(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_VAULT_SECURITY_SETTINGS.session_timeout_minutes;
  return Math.min(480, Math.max(5, Math.round(value)));
}

export function clampPasswordMinLength(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_VAULT_SECURITY_SETTINGS.password_min_length;
  return Math.min(64, Math.max(SIMAPPE_PASSWORD_MIN_LENGTH, Math.round(value)));
}

export function resolveVaultPasswordMinLength(): number {
  return Math.max(SIMAPPE_PASSWORD_MIN_LENGTH, readVaultSecuritySettings().password_min_length);
}

export function readVaultSessionInactivityMinutes(): number | null {
  if (typeof localStorage === 'undefined') return null;
  if (!localStorage.getItem(VAULT_SECURITY_SETTINGS_STORAGE_KEY)) return null;
  return readVaultSecuritySettings().session_timeout_minutes;
}

export function validateVaultPasswordAgainstPolicy(password: string): string | null {
  const policy = readVaultSecuritySettings();
  const minLength = resolveVaultPasswordMinLength();
  const trimmed = password.trim();
  if (!trimmed) {
    return 'La contraseña es obligatoria.';
  }
  if (trimmed.length < minLength) {
    const simappeNote =
      minLength === SIMAPPE_PASSWORD_MIN_LENGTH && policy.password_min_length < SIMAPPE_PASSWORD_MIN_LENGTH
        ? ' (requisito Simappe)'
        : '';
    return `La contraseña debe tener al menos ${minLength} caracteres${simappeNote}.`;
  }
  if (policy.require_uppercase && !/[A-Z]/.test(trimmed)) {
    return 'La contraseña debe incluir al menos una letra mayúscula.';
  }
  if (policy.require_numbers && !/\d/.test(trimmed)) {
    return 'La contraseña debe incluir al menos un número.';
  }
  if (policy.require_special_chars && !/[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;/'`~]/.test(trimmed)) {
    return 'La contraseña debe incluir al menos un carácter especial.';
  }
  return null;
}

/** Validación Simappe al crear o resetear clave (8–255). Sin reglas de política local. */
export function validateSimappeProvisionPassword(password: string): string | null {
  const trimmed = password.trim();
  if (!trimmed) {
    return 'La contraseña es obligatoria.';
  }
  if (trimmed.length < SIMAPPE_PASSWORD_MIN_LENGTH) {
    return 'La contraseña debe tener al menos 8 caracteres.';
  }
  if (trimmed.length > SIMAPPE_PASSWORD_MAX_LENGTH) {
    return 'La contraseña no puede superar 255 caracteres.';
  }
  return null;
}

export function notifyVaultSecuritySettingsUpdated(): void {
  if (typeof globalThis.window === 'undefined') return;
  globalThis.window.dispatchEvent(new CustomEvent(VAULT_SECURITY_SETTINGS_UPDATED_EVENT));
}
